<template>
    <div v-loading="loading" class="abbreviations">

        <el-alert :title="$t('abbreviations.newUploadsOnly')"
                  type="info"
                  :closable="false"
                  show-icon />

        <p class="description">{{ $t('abbreviations.description') }}</p>

        <div v-for="category in categories" :key="category.key" class="category">

            <div class="category-header">
                <h4>{{ $t(category.titleKey) }}</h4>

                <el-button type="danger"
                           link
                           :disabled="saving || effectiveEntries(category).length === 0"
                           @click="clearCategory(category)">
                    {{ $t('abbreviations.clearAll') }}
                </el-button>
            </div>

            <p class="hint">{{ $t(category.hintKey) }}</p>

            <div class="entries">
                <el-tag v-for="entry in effectiveEntries(category)"
                        :key="entry.value"
                        :type="entry.isBuiltIn ? 'info' : 'success'"
                        closable
                        :disable-transitions="true"
                        @close="removeEntry(category, entry)">
                    {{ entry.value }}
                </el-tag>

                <span v-if="effectiveEntries(category).length === 0" class="empty">
                    {{ $t('abbreviations.empty') }}
                </span>
            </div>

            <div class="add-row">
                <el-input v-model="newEntries[category.key]"
                          :placeholder="$t('abbreviations.addPlaceholder')"
                          :disabled="saving"
                          maxlength="20"
                          @keyup.enter="addEntry(category)" />

                <el-button type="primary"
                           :disabled="saving"
                           @click="addEntry(category)">
                    {{ $t('abbreviations.add') }}
                </el-button>
            </div>

            <p v-if="errors[category.key]" class="error">{{ errors[category.key] }}</p>
        </div>

        <div class="actions">
            <el-button type="primary"
                       :loading="saving"
                       :disabled="!hasUnsavedChanges"
                       @click="save">
                {{ $t('generic.save') }}
            </el-button>

            <el-button :disabled="saving || !hasSuppressedEntries"
                       @click="restoreDefaults">
                {{ $t('abbreviations.restoreDefaults') }}
            </el-button>

            <el-tag v-if="hasUnsavedChanges" type="warning" :disable-transitions="true">
                {{ $t('abbreviations.unsavedChanges') }}
            </el-tag>
        </div>

    </div>
</template>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { userSettingsService } from '@/services/userSettingsService';
    import { notifySuccess, notifyWarning } from '@/services/notificationService';
    import { notifyMaintenance } from '@/utils/httpClient';

    import {
        AbbreviationDefaults,
        AbbreviationSettings,
        abbreviationEntryLimit,
        abbreviationPartLimit,
        emptyAbbreviationSettings
    } from '@/models/AbbreviationSettings';

    interface CategoryDescriptor {
        key: 'neverSplit' | 'maySplit',
        addedKey: 'neverSplitAdded' | 'maySplitAdded',
        suppressedKey: 'neverSplitSuppressed' | 'maySplitSuppressed',
        titleKey: string,
        hintKey: string,
    }

    interface CategoryEntry {
        value: string,
        isBuiltIn: boolean,
    }

    export default defineComponent({
        data() {
            return {
                userSettingsService: new userSettingsService(),

                loading: false,
                saving: false,

                defaults: { neverSplit: [], maySplit: [] } as AbbreviationDefaults,
                settings: emptyAbbreviationSettings() as AbbreviationSettings,

                savedSettings: '',

                newEntries: { neverSplit: '', maySplit: '' } as Record<string, string>,
                errors: { neverSplit: '', maySplit: '' } as Record<string, string>,

                categories: [
                    {
                        key: 'neverSplit',
                        addedKey: 'neverSplitAdded',
                        suppressedKey: 'neverSplitSuppressed',
                        titleKey: 'abbreviations.neverSplitTitle',
                        hintKey: 'abbreviations.neverSplitHint',
                    },
                    {
                        key: 'maySplit',
                        addedKey: 'maySplitAdded',
                        suppressedKey: 'maySplitSuppressed',
                        titleKey: 'abbreviations.maySplitTitle',
                        hintKey: 'abbreviations.maySplitHint',
                    },
                ] as CategoryDescriptor[],
            }
        },
        created() {
            this.init();
        },
        computed: {
            hasSuppressedEntries(): boolean {
                return this.categories.some(category => this.settings[category.suppressedKey].length > 0);
            },
            hasUnsavedChanges(): boolean {
                return this.fingerprint(this.settings) !== this.savedSettings;
            },
        },
        methods: {
            async init() {
                this.loading = true;

                const [defaults, settings] = await Promise.all([
                    this.userSettingsService.getAbbreviationDefaults(),
                    this.userSettingsService.getAbbreviations(),
                ]);

                this.defaults = defaults;
                this.settings = settings;
                this.savedSettings = this.fingerprint(settings);

                this.loading = false;
            },
            effectiveEntries(category: CategoryDescriptor): CategoryEntry[] {
                const suppressed = this.settings[category.suppressedKey];

                const builtIns = this.defaults[category.key]
                    .filter(entry => !suppressed.includes(entry))
                    .map(entry => ({ value: entry, isBuiltIn: true }));

                const added = this.settings[category.addedKey]
                    .map(entry => ({ value: entry, isBuiltIn: false }));

                return [...builtIns, ...added].sort((left, right) => left.value.localeCompare(right.value));
            },
            addEntry(category: CategoryDescriptor) {
                this.errors[category.key] = '';

                const entry = this.normalize(this.newEntries[category.key]);

                if (entry.length === 0) {
                    return;
                }

                if (!this.isValidEntry(entry)) {
                    this.errors[category.key] = this.$t('abbreviations.invalidEntry');
                    return;
                }

                if (this.effectiveEntries(category).some(existing => existing.value === entry)) {
                    this.errors[category.key] = this.$t('abbreviations.duplicateEntry');
                    return;
                }

                if (this.effectiveEntries(category).length >= abbreviationEntryLimit) {
                    this.errors[category.key] = this.$t('abbreviations.limitReached', { limit: abbreviationEntryLimit });
                    return;
                }

                const suppressed = this.settings[category.suppressedKey];
                const suppressedIndex = suppressed.indexOf(entry);

                if (suppressedIndex >= 0) {
                    suppressed.splice(suppressedIndex, 1);
                } else {
                    this.settings[category.addedKey].push(entry);
                }

                this.newEntries[category.key] = '';
            },
            removeEntry(category: CategoryDescriptor, entry: CategoryEntry) {
                this.errors[category.key] = '';

                if (entry.isBuiltIn) {
                    this.settings[category.suppressedKey].push(entry.value);
                    return;
                }

                const added = this.settings[category.addedKey];
                const addedIndex = added.indexOf(entry.value);

                if (addedIndex >= 0) {
                    added.splice(addedIndex, 1);
                }
            },
            restoreDefaults() {
                this.categories.forEach(category => {
                    this.errors[category.key] = '';
                    this.settings[category.suppressedKey] = [];
                });
            },
            clearCategory(category: CategoryDescriptor) {
                this.errors[category.key] = '';

                this.settings[category.suppressedKey] = [...this.defaults[category.key]];
                this.settings[category.addedKey] = [];
            },
            async save() {
                this.saving = true;

                try {
                    await this.userSettingsService.saveAbbreviations(this.settings);
                    this.savedSettings = this.fingerprint(this.settings);

                    notifySuccess(this.$t('abbreviations.saved'), this.$t('abbreviations.newUploadsOnly'));
                } catch (error: any) {
                    if (!notifyMaintenance(error)) {
                        const rejected = error?.response?.data?.invalidEntries ?? [];

                        notifyWarning(
                            this.$t('abbreviations.saveFailed'),
                            rejected.length > 0
                                ? this.$t('abbreviations.rejectedEntries', { entries: rejected.join(', ') })
                                : this.$t('abbreviations.saveFailedMessage'));
                    }
                }

                this.saving = false;
            },
            fingerprint(settings: AbbreviationSettings) {
                return JSON.stringify([
                    [...settings.neverSplitAdded].sort(),
                    [...settings.neverSplitSuppressed].sort(),
                    [...settings.maySplitAdded].sort(),
                    [...settings.maySplitSuppressed].sort(),
                ]);
            },
            normalize(entry: string) {
                return (entry ?? '').trim().toLowerCase();
            },
            isValidEntry(entry: string) {
                if (!entry.endsWith('.')) {
                    return false;
                }

                if (!/^[\p{L}.]+$/u.test(entry)) {
                    return false;
                }

                const parts = entry.split('.');
                const significantParts = parts.slice(0, parts.length - 1);

                if (significantParts.length === 0 || significantParts.length > abbreviationPartLimit) {
                    return false;
                }

                return significantParts.every(part => part.length > 0);
            },
        }
    })
</script>

<style lang="scss" scoped>
    .description {
        margin-top: 16px;
    }

    .category {
        margin-bottom: 28px;
        padding: 16px;
        border-radius: 4px;
        background-color: var(--el-fill-color-light);
    }

    .category-header {
        display: flex;
        align-items: center;
        justify-content: space-between;

        h4 {
            margin: 0;
        }
    }

    .hint {
        margin: 8px 0 12px 0;
        color: var(--el-text-color-secondary);
    }

    .entries {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
    }

    .empty {
        color: var(--el-text-color-secondary);
    }

    .add-row {
        display: flex;
        gap: 8px;
        max-width: 420px;
    }

    .error {
        margin: 8px 0 0 0;
        color: var(--el-color-danger);
    }

    .actions {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 8px;
    }
</style>
